/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Form, Label, Input } from 'reactstrap';
import { SelectSingleDatasetNew, SelectSingleSecret, SelectFileStrategy, SelectAudioModel, SelectAudioVoice } from '../Chat/ChatBox.js'
import { v4 as uuidv4 } from "uuid";
import { validateFile, detectDelimiter } from '../Utils/file_functions.js'
import { escapeHTML } from '../Utils/text_functions.js'
import { Dialog, DialogActions, DialogTitle, IconButton, Box, Button } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { ScrollableDialogContent } from '../Components/ScrollableDialogContent.js';
import { sanitizeHtml } from '../Utils/text_functions';
import CsvUploaderPopup from '../Popups/CsvUploaderPopup.js'

import * as log from 'loglevel'

const ShowPlugin = React.forwardRef(({ default_data, showAddDataset, handleBotMessage, showPromptList, cur_plugin, secrets, datasets, datasetPermissions, setTriggerPluginList, handlePluginClick, triggerShowPlugin, setTriggerShowPlugin, getToken }, ref) => {
  const [loaded, setLoaded] = useState(false);
  var loaded_static = false;

  const [files, setFiles] = useState({});
  const [fileStrategies, setFileStrategies] = useState({});
  const [triggerCsvUploaderPopup, setTriggerCsvUploaderPopup] = useState(0);

  useImperativeHandle(ref, () => ({
    handlePromptClickExec
  }));

  const handlePromptClickExec = (field_name, value) => {
    const updatedFormValues = { ...formValues };
    updatedFormValues[field_name] = value;
    setFormValues(updatedFormValues);
  }
  
  const handleFileChange = (event, fieldName) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFiles(prevFiles => ({ ...prevFiles, [fieldName]: selectedFile }));
      setErrorMessage('');
    } else {
      setFiles(prevFiles => ({ ...prevFiles, [fieldName]: null }));
      setErrorMessage('Invalid file type or size');
    }
  };

  const getFirstFile = () => {
    if(files) {
      for (const [fieldName, file] of Object.entries(files)) {
        return file;
      }
    }
    return null;
  };

  const handleFileStrategyChange = (event, fieldName) => {
    const { value } = event.target;
    setFileStrategies(prevStrategies => ({ ...prevStrategies, [fieldName]: value }));
  };

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState([]);

  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const [formValues, setFormValues] = useState({});

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleDatasetChange = (name, event) => {
    const value = event;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { name } = event.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: prevValues[name] === 'on' ? '' : 'on' }));
  };

  function updatePlugin() {
    setIsLoading(false);
    setErrorMessage('');
    setSuccessMessage('');
    setInputs([]);
    setInputs(cur_plugin.fields);
    setAudioBlob(null);
    setAudioUrl(null);
    setTriggerCsvUploaderPopup(0);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    setErrorMessage('');
    setSuccessMessage('');

    let failed = false;
    let cur_form_values = { ...formValues };

    for (const field of cur_plugin.fields) {
      const { name, type, required, pattern, default_value } = field;
      let cur_value = formValues[name];

      if (type !== 'checkbox' && type !== 'dataset' && cur_value === undefined) {
        cur_value = default_value;
        cur_form_values[name] = cur_value;
      }
      if (type === 'checkbox' && (cur_value === '' || cur_value === undefined)) {
        cur_form_values[name] = 'off';
      }
      if (type === 'dataset' && cur_value !== '' && cur_value !== undefined && cur_value !== null) {
        // check if cur_value is string
        if (typeof cur_value === 'string') {
          cur_form_values[name] = cur_value;
        } else {
          cur_form_values[name] = cur_value.value;
        }
      }

      if (required && required === 'required' && (cur_value == null || cur_value === undefined || cur_value === '')) {
        setErrorMessage('You must complete the required fields');
        failed = true;
        break;
      }

      if (pattern && cur_value !== '' && cur_value !== undefined && !new RegExp(pattern.slice(1, -1)).test(cur_value)) {
        setErrorMessage(`The field ${name} must match the format required.`);
        failed = true;
        break;
      }
    }

    if (failed) {
      setIsLoading(false);
      return;
    }

    let handled = false;

    var file = getFirstFile();
    if (cur_plugin.plugin_name === 'TEXT_TO_SPEECH') {
      const token = getToken();
      if (!token) {
        return;
      }

      try {        
        const response = await fetch(`${default_data.default_chat_service_url}/get-text-to-speech`, {
          method: 'POST',
          headers: {
            'x-access-tokens': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'text': cur_form_values['text'],
            'model': cur_form_values['audio_model'],
            'voice': cur_form_values['audio_voice'],
          }),
          timeout: 1200000,
        });
     
        if (!response.ok) {
          throw new Error('Failed to submit text to speech request');
        }
        const contentType = response.headers.get('Content-Type');

        let data;
        if (contentType && contentType.includes('application/json')) {
          // If the response is JSON
          data = await response.json();
          setErrorMessage(data.response);
          setSuccessMessage('')
          setIsLoading(false);
          return; 
        } else {
          // If the response is a Blob
          data = await response.blob();
          setAudioBlob(data);
          const url = window.URL.createObjectURL(data);
          setAudioUrl(url);

          setErrorMessage('')
          setSuccessMessage('Text to speech generated. Please download the file as we do not store it for you.');
          setIsLoading(false);
        }
      }
      catch (error) {
        setErrorMessage('Internal error, please contact customer support');
        setSuccessMessage('');
        log.error(`/showPlugin: ${error.message}`);
        setIsLoading(false);
      }
      return;
    }
    else if (cur_plugin.plugin_name === 'FILTER_INGEST_CSV') {
      try {
        if (!file) {
          setErrorMessage('Please select a file');
          setIsLoading(false);
          return;
        }
        // check file type is csv
        if (!file.name.endsWith('.csv')) {
          setErrorMessage('Please select a CSV file');
          setIsLoading(false);
          return;
        }

        // check file MIME type
        if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
          setErrorMessage('The file is not a valid CSV');
          setIsLoading(false);
          return;
        }

        // check file content for CSV characteristics and binary signature
        const reader = new FileReader();
        reader.onload = function(event) {
          const text = event.target.result;
          const lines = text.split('\n');
          const delimiter = detectDelimiter(lines[0]);

          if (!delimiter) {
            setErrorMessage('The file content does not appear to be a valid CSV');
            setIsLoading(false);
            return;
          }

          // Check for non-text binary content
          const binaryReader = new FileReader();
          binaryReader.onload = function(event) {
            const arrayBuffer = event.target.result;
            const uint8Array = new Uint8Array(arrayBuffer);

            // Check for common binary file signatures (magic numbers)
            const binarySignatures = [
              [0x50, 0x4B, 0x03, 0x04], // ZIP (common for XLSX, DOCX, etc.)
              [0x25, 0x50, 0x44, 0x46], // PDF
              [0x89, 0x50, 0x4E, 0x47], // PNG
              [0xFF, 0xD8, 0xFF],       // JPEG
              [0x47, 0x49, 0x46, 0x38], // GIF
              [0x42, 0x4D],             // BMP
              [0xD0, 0xCF, 0x11, 0xE0], // Older MS Office files (DOC, XLS, PPT)
            ];

            for (let signature of binarySignatures) {
              let match = true;
              for (let i = 0; i < signature.length; i++) {
                if (uint8Array[i] !== signature[i]) {
                  match = false;
                  break;
                }
              }
              if (match) {
                setErrorMessage('The file appears to be a binary file, not a CSV');
                setIsLoading(false);
                return;
              }
            }

            setTriggerCsvUploaderPopup(1);
            setIsLoading(false);
          };
          binaryReader.onerror = function() {
            setErrorMessage('Error reading the file');
            setIsLoading(false);
          };
          binaryReader.readAsArrayBuffer(file);
        };
        reader.onerror = function() {
          setErrorMessage('Error reading the file');
          setIsLoading(false);
        };
        reader.readAsText(file);
      }
      catch (error) {
        setErrorMessage('Internal error, please contact customer support');
        setSuccessMessage('');
        log.error(`/showPlugin: handleSubmit: ${error.message}`);
        setIsLoading(false);
      }
      return;
    }
    else if (cur_plugin.file_mode === true) {
      try {
        if (!file) {
          setErrorMessage('Please select a file');
          setIsLoading(false);
          return;
        }
        // check file type is csv
        if (!file.name.endsWith('.csv')) {
          setErrorMessage('Please select a CSV file');
          setIsLoading(false);
          return;
        }

        // check file MIME type
        if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
          setErrorMessage('The file is not a valid CSV');
          setIsLoading(false);
          return;
        }

        // check file content for CSV characteristics and binary signature
        const reader = new FileReader();
        reader.onload = function(event) {
          const text = event.target.result;
          const lines = text.split('\n');
          const delimiter = detectDelimiter(lines[0]);

          if (!delimiter) {
            setErrorMessage('The file content does not appear to be a valid CSV');
            setIsLoading(false);
            return;
          }

          // Check for non-text binary content
          const binaryReader = new FileReader();
          binaryReader.onload = function(event) {
            const arrayBuffer = event.target.result;
            const uint8Array = new Uint8Array(arrayBuffer);

            // Check for common binary file signatures (magic numbers)
            const binarySignatures = [
              [0x50, 0x4B, 0x03, 0x04], // ZIP (common for XLSX, DOCX, etc.)
              [0x25, 0x50, 0x44, 0x46], // PDF
              [0x89, 0x50, 0x4E, 0x47], // PNG
              [0xFF, 0xD8, 0xFF],       // JPEG
              [0x47, 0x49, 0x46, 0x38], // GIF
              [0x42, 0x4D],             // BMP
              [0xD0, 0xCF, 0x11, 0xE0], // Older MS Office files (DOC, XLS, PPT)
            ];

            for (let signature of binarySignatures) {
              let match = true;
              for (let i = 0; i < signature.length; i++) {
                if (uint8Array[i] !== signature[i]) {
                  match = false;
                  break;
                }
              }
              if (match) {
                setErrorMessage('The file appears to be a binary file, not a CSV');
                setIsLoading(false);
                return;
              }
            }

            var token = getToken();
            if (token === false) {
              setErrorMessage('Please log in to use this feature');
              setIsLoading(false);
              return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('plugin_name', cur_plugin.plugin_name);
        
            try {
              fetch(default_data.default_chat_service_url + '/execute-plugin-with-file', {
                method: 'POST',
                headers: {
                  'x-access-tokens': `${token}`
                },        
                body: formData,
                timeout: 3600000 // 60 minutes in milliseconds
              })
              .then(response => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error('An error occurred while getting data.');
              })
              .then(data => {
                setIsLoading(false);
                if(data['status'] === 200) {
                  if (data['message'].includes('Sorry,')) {
                    setErrorMessage(data['message'])
                  }
                  else {
                    handleBotMessage({
                      id: uuidv4(),
                      user: "gpt",
                      message_orig: data['message'],
                      message: escapeHTML(data['message']),
                      embedding_down: false,
                      vectors_down: false,
                      added_obj: null,
                      analysis_failed: false,
                      references: null,
                      type: 'teach'
                    });
                    closeModal();
                    setTriggerPluginList(0);
                  }
                }
                else {
                  setIsLoading(false);
                  if (data['message'].includes('Sorry,')) {
                    setErrorMessage(data['message'])
                  }
                  else {
                    setErrorMessage('Internal error, please contact the customer support [1].')
                  }
                } 
              })       
              .catch(error => {
                console.log(error);
                setErrorMessage('Internal error, please contact the customer support [2].')
              });  
            } catch (error) {
              setErrorMessage('Internal error, please contact the customer support [3].')
            }          
            setIsLoading(false);
          };
          binaryReader.onerror = function() {
            setErrorMessage('Error reading the file');
            setIsLoading(false);
          };
          binaryReader.readAsArrayBuffer(file);
        };
        reader.onerror = function() {
          setErrorMessage('Error reading the file');
          setIsLoading(false);
        };
        reader.readAsText(file);
      }
      catch (error) {
        setErrorMessage('Internal error, please contact customer support');
        setSuccessMessage('');
        log.error(`/showPlugin: handleSubmit: 3: ${error.message}`);
        setIsLoading(false);
      }
      return;
    }

    for (const [fieldName, file] of Object.entries(files)) {
      const token = getToken();
      if (!token) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('strategy', fileStrategies[fieldName] || 'auto');

      try {
        const special_csv = cur_plugin.plugin_name === 'INGEST_CSV' || cur_plugin.plugin_name === 'ITERATIVE_CSV';
        formData.append('special_csv', special_csv);

        const response = await fetch(`${default_data.default_chat_service_url_files}/file`, {
          method: 'POST',
          headers: {
            'x-access-tokens': token,
            'Connection': 'keep-alive'
          },
          body: formData,
          timeout: 1200000,
        });

        const data = await response.json();
        let ret = 'Internal error, please contact customer support with more information about your file';

        if (cur_plugin.plugin_name === 'OPEN_FILE') {
          if(data['status'] == '200') {
            if(typeof data['ret'] === 'object') {
              if('text' in data['ret']) {
                ret = data['ret'].text;
                ret = 'File successfully uploaded and processed, please find the text/plain below\n\n' + ret
              }
              else {
                ret = 'Internal error, please contact customer support with more information about your file [2]'
              }
            }
            else {
              ret = 'File successfully uploaded and processed, please find the text/plain below\n\n' + data['ret']
            }
          }  
          else if(data['response'] === 'No file too large') {
            ret = 'File too large, please contact customer support with more information about your file'
          }           
          else if(data['response'] === 'Virus detected in file') {
            ret = 'Virus detected in file, please contact customer support with more information about your file'
          }           
          else if(data['response'] === 'File type not allowed') {
            ret = 'File type not allowed, please contact customer support with more information about your file'
          }

          ret = escapeHTML(ret);

          handleBotMessage({
            id: uuidv4(),
            user: "gpt",
            message_orig: ret,
            message: ret,
            embedding_down: false,
            vectors_down: false,
            added_obj: null,
            analysis_failed: false,
            references: null,
            type: 'teach'
          });
          handled = true;

        } else {
          if(data['status'] == '200') {
            if(typeof data['ret'] === 'object') {
              if('text' in data['ret']) {
                ret = data['ret'].text;
              }
              else {
                ret = 'Internal error, please contact customer support with more information about your file [2]'
                handleBotMessage({
                  id: uuidv4(),
                  user: "gpt",
                  message_orig: ret,
                  message: ret,
                  embedding_down: false,
                  vectors_down: false,
                  added_obj: null,
                  analysis_failed: false,
                  references: null,
                  type: 'teach'
                });
                handled = true;
              }
            }
            else {
              ret = data['ret']
            }
          
            cur_form_values[fieldName] = ret;
          }
          else {
            handleBotMessage({ id: uuidv4(), user: "gpt", message_orig: ret, message: ret, embedding_down: false, vectors_down: false, added_obj: null, analysis_failed: false, references:null, type: 'teach'})
            handled = true;
          }
        }

      } catch (error) {
        console.log(error);
      }
    }

    if(!handled) {
      var prompt_template = cur_plugin['prompt_template'];
      prompt_template = prompt_template.replace(/{/g, "{custom_field_");
  
      var prompt = prompt_template.replace(/{custom_field_([^}]+)}/g, (_, key) => cur_form_values[key]);
      
      await handlePluginClick(cur_plugin.plugin_name, cur_form_values, prompt);
    }

    setIsLoading(false);
    closeModal();
    setTriggerPluginList(0);
  };

  function openModal() {
    setIsOpen(true);
  }

  function afterOpenModal() {
    // references are now sync'd and can be accessed.
  }

  function closeModal() {
    setTriggerShowPlugin(0);
    setIsOpen(false);
  }

  useEffect(() => {
    if (triggerShowPlugin === 1) {
      updatePlugin();
      setIsOpen(true);
    }
  }, [triggerShowPlugin]);

  useEffect(() => {
    if (loaded || loaded_static) {
      return;
    }

    setLoaded(true);
    loaded_static = true;
  }, []);

  const handleDownload = () => {
    if (audioBlob) {
      const url = window.URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'speech.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <>
    <Dialog
      open={modalIsOpen}
      onClose={closeModal}
      maxWidth='lg'
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Box display={'flex'} justifyContent={'space-between'}>
          {cur_plugin.title}
          <IconButton onClick={closeModal}>
            <IoClose />
          </IconButton>
        </Box>
      </DialogTitle>
      <ScrollableDialogContent modalIsOpen={modalIsOpen}>
      <div tag={cur_plugin.id} className="plugin-item-description">
          {(cur_plugin.description)}
      </div>
      <Form>
        <div style={{margin: '20px 0'}}>
        {inputs.map((field) => (
          <div style={{margin: '20px 0'}} key={field.name}>
            <Label htmlFor={field.name} dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.description) }}/>
            {field.type === 'textarea_prompt' ? (
              <span>
                <Input
                  type="textarea"
                  required={field.required ? 'required' : ''} 
                  rows="5"
                  name={field.name} 
                  value={formValues[field.name] || field.default_value || ''}
                  onChange={handleInputChange} 
                />
                <Button 
                  variant='outlined' 
                  style={{
                    marginTop: '3px',
                    borderWidth: '1px',
                    fontWeight: 500,
                  }} 
                  onClick={() => showPromptList("plugin", field.name)} 
                  size="small"
                >
                  Pick a prompt from prompt templates
                </Button>
              </span>
            ) : field.type === 'textarea' ? (
              <Input type="textarea" required={field.required ? 'required' : ''} 
                rows="5" name={field.name} 
                value={formValues[field.name] || field.default_value || ''}
                onChange={handleInputChange} />
            ) : field.type === 'secret' ? (
              <span style={{marginLeft: '10px'}}><SelectSingleSecret value={formValues[field.name]} secrets={secrets} onChange={handleInputChange}></SelectSingleSecret></span>
            ) : field.type === 'dataset' ? (
              <span style={{marginLeft: '10px'}}><SelectSingleDatasetNew noAllNone="true" value={formValues[field.name]} datasetPermissions={datasetPermissions} datasets={datasets} onChange={(e) => { handleDatasetChange(field.name, e);}} default_data={default_data}></SelectSingleDatasetNew> {showAddDataset && <Button style={{marginLeft: '10px'}} onClick={showAddDataset}>Create New Dataset</Button>}
              </span>
            ) : field.type === 'file_strategy' ? (
              <span style={{marginLeft: '10px'}}><SelectFileStrategy onChange={handleFileStrategyChange}></SelectFileStrategy></span>
            ) : field.type === 'audio_voice' ? (
              <span style={{marginLeft: '10px'}}><SelectAudioVoice onChange={handleInputChange}></SelectAudioVoice></span>
            ) : field.type === 'audio_model' ? (
              <span style={{marginLeft: '10px'}}><SelectAudioModel onChange={handleInputChange}></SelectAudioModel></span>
            ) : field.type === 'file' ? (
              <span style={{ marginLeft: '10px' }}><Input name={field.name} type="file" onChange={(e) => handleFileChange(e, field.name)} /></span>
            ) : field.type === 'checkbox' ? (
              <Input type="checkbox" 
                id={field.name} style={{marginLeft: '10px'}}
                name={field.name}
                checked={formValues[field.name] || false} // Ensure checked is always a boolean
                onChange={handleCheckboxChange}
              />
            ) : (
              <Input type="text" 
                id={field.name}
                name={field.name}
                value={formValues[field.name] || field.default_value || ''}
                onChange={handleInputChange}
              />
            )}
          </div>
        ))}
        </div>
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}

        { audioBlob !== null ?
          <div>
            <Button onClick={handleDownload} disabled={!audioBlob}>Download Audio</Button><br />
            {audioUrl && <audio src={audioUrl} controls /> }<br /><br />
          </div> 
          : '' 
        }

      </Form>
      </ScrollableDialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isLoading} variant='contained'>
          {isLoading ? 'Loading...' : 'Submit'}
        </Button>
        <Button onClick={closeModal} color="error">
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <CsvUploaderPopup default_data={default_data} formValues={formValues} file={getFirstFile()} setTriggerCsvUploaderPopup={setTriggerCsvUploaderPopup} triggerCsvUploaderPopup={triggerCsvUploaderPopup} getToken={getToken} />
    </>
  );
});

export default ShowPlugin;